import { Component, OnInit, Output, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

/**
 * A drop-down of single-value temporal searches.  Used in both the Listing Reservations and the Finanical Summary Components.
 * Properties:
 *   showSearchButton = controls whether the seach button is shown (only set to false if searchOnSelect is set to true)
 *   searchOnSelect = when true, the searchTypeChanged event is immediately fired.
 *   disabled = sets the component to disabled
 *   waiting = sets the component to waiting (which will temporarily disable the component). Usually this will be bound to a waiting property of the parent component.
 *   selectOptions = overrides the internal default select options
 *
 * <temporal-select [showSearchButton]="true" [waiting]="waiting" [disabled]="false" (searchTypeChanged)="onSearchTypeChanged($event)"></temporal-select>
 */
@Component({
    selector: "temporal-select",
    templateUrl: "./temporal-select.component.html",
    styleUrls: ["./temporal-select.component.less"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, FormsModule]
})
export class TemporalSelectComponent implements OnInit {
  private readonly _componentName = 'TemporalSelectComponent';

  /**
   * Backing field for the Search Type Property
   */
  private _searchType: string = 'All';

  /**
   * Backing field for the searchOnSelect property
   */
  private _searchOnSelect: boolean = true;

  /**
   * Backing field for the disabled property
   */
  private _disabled: boolean;

  /**
   * Backing field for the waiting property
   */
  private _waiting: boolean;

  /**
   * Backing field for the showSearchButton property
   */
  private _showSearchButton: boolean = true;

  @Output()
  public searchTypeChanged = new EventEmitter<string>();

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngOnInit() {}

  /**
   * Getter for searchType property
   */
  public get searchType(): string {
    return this._searchType;
  }

  /**
   * Setter for searchType property
   */
  public set searchType(v: string) {
    this._searchType = v;

    if (this.searchOnSelect) {
      this.search();
    }
  }

  /**
   * Controls the behavior when a select option is changed. If true, the component
   * will immediately emit a changed event.  Otherwise, the submit button must be pressed.
   */
  public get searchOnSelect(): boolean {
    return this._searchOnSelect;
  }

  @Input()
  public set searchOnSelect(v: boolean) {
    this._searchOnSelect = v;
  }

  /**
   * Property indicating if the component is disabled
   */
  public get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  public set disabled(v: boolean) {
    this._disabled = v;
  }

  /**
   * Property for when the component is set to waiting
   */
  public get waiting(): boolean {
    return this._waiting;
  }

  @Input()
  public set waiting(v: boolean) {
    this._waiting = v;

    if (this.waiting) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }
  }

  /**
   * Controls visibility of the search button. Default: True
   * Only set to false if the searchOnSelect property is set to true;
   */
  public get showSearchButton(): boolean {
    // console.log(`${this._componentName}.showSearchButton() return ${this._showSearchButton}`);

    return this._showSearchButton;
  }

  @Input()
  public set showSearchButton(v: boolean) {
    this._showSearchButton = v;
    this.changeDetector.detectChanges();
  }

  /**
   * Options for the Temporal Select Control.
   * Initialized with internal default, but can
   * be input via template binding
   */
  public get selectOptions(): Array<any> {
    return this._selectOptions;
  }

  @Input()
  public set selectOptions(v: Array<any>) {
    this._selectOptions = v;
  }

  /**
   * Search Button is Enabled
   */
  public get searchButtonEnabled(): boolean {
    if (this.disabled) {
      // console.log(`${this._componentName}.searchButtonEnabled() return false because disabled is true`);
      return false;
    }

    if (this.waiting) {
      // console.log(`${this._componentName}.searchButtonEnabled() return false because waiting is true`);
      return false;
    } else {
      // console.log(`${this._componentName}.searchButtonEnabled() return true because waiting is false`);
      return true;
    }
  }

  /**
   * Executes the search
   */
  public search() {
    console.log(`${this._componentName}.search() searchType=`, this.searchType);

    this.searchTypeChanged.emit(this.searchType);

    console.log(`${this._componentName}.search() Return`);
  }

  /**
   * Options for the Temporal Select Control.
   * Initialized with internal default, but can
   * be input via template binding
   */
  private _selectOptions: Array<any> = [
    {
      value: 'All',
      label: 'All'
    },
    {
      value: 'AllFuture',
      label: 'Future (All)'
    },
    {
      value: 'AllPast',
      label: 'Past (All)'
    },
    {
      value: 'LastMonth',
      label: 'Last Month'
    },
    {
      value: 'ThisMonth',
      label: 'This Month'
    },
    {
      value: 'YTD',
      label: 'YTD'
    },
    {
      value: 'PreviousYTD',
      label: 'Previous YTD'
    },
    {
      value: 'PreviousYear',
      label: 'Previous Year'
    }
  ];
}
